import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, isRef as _isRef, createVNode as _createVNode, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { class: "flex flex-col items-start gap-4 w-full animate-slideFadeIn" }
const _hoisted_2 = {
  key: 0,
  class: "mb-4"
}
const _hoisted_3 = {
  key: 1,
  class: "text-xs text-red-600 bg-red-100 px-3 py-2 rounded-lg w-full"
}
const _hoisted_4 = ["disabled"]

import { ref, computed } from "vue"
import { useStore } from "vuex"
import { useForm, useField } from "vee-validate"
import * as Yup from "yup"
import FormInput from "../auth/FormInput.vue"
import api from "@/services/api"


export default /*@__PURE__*/_defineComponent({
  __name: 'SocialLinksStep',
  props: {
    canGoBack: { type: Boolean }
  },
  emits: ["next", "back"],
  setup(__props: any, { emit: __emit }) {

const emit = __emit


const store = useStore()
const isLoading = ref(false)
const extractUsername = (input: string) => {
    if (!input) return ""
    const match = input.match(/(?:instagram\.com|tiktok\.com)\/@?([A-Za-z0-9._]+)/i)
    return match ? match[1] : input.replace(/^@/, "")
}

// IG conditions : 
// no Consecutive dots , can't start or end with dot , 1-30 chars 
// allowed : dot , underscore , alphanumeric
// Tiktok conditions: 
// no space, no special chars except dot, no consecutive dots , can't start or end with dot,2-24 chars
// allowed : dot , underscore , alphanumeric
const isLatinOnly = (value: string) => /^[A-Za-z0-9._@:/-]+$/.test(value)

const schema = Yup.object({
    instagram: Yup.string()
        .nullable()
        .test(
            "latin-only",
            "Username must contain only Latin characters",
            (value?: string | null) => {
                if (!value) return true
                return isLatinOnly(value)
            }
        )
        .test(
            "valid-instagram",
            "Invalid Instagram username",
            (value?: string | null) => {
                if (!value) return true
                const username = extractUsername(value)
                return /^(?!.*\.\.)(?!\.)(?!.*\.$)[A-Za-z0-9._]{1,30}$/.test(username)
            }
        ),

    tiktok: Yup.string()
        .nullable()
        .test(
            "latin-only",
            "Username must contain only Latin characters",
            (value?: string | null) => {
                if (!value) return true
                return isLatinOnly(value)
            }
        )
        .test(
            "valid-tiktok",
            "Invalid TikTok username",
            (value?: string | null) => {
                if (!value) return true
                const username = extractUsername(value)
                return /^(?!.*\.\.)(?!\.)(?!.*\.$)[A-Za-z0-9._]{2,24}$/.test(username)
            }
        ),
})

const { handleSubmit } = useForm({
    validationSchema: schema,
})

const { value: instagram, errorMessage: instagramError, meta: instagramMeta } =
    useField<string>("instagram", undefined, { initialValue: store.state.authOnboarding.ig_username || "" })



const { value: tiktok, errorMessage: tiktokError, meta: tiktokMeta } =
    useField<string>("tiktok", undefined, { initialValue: store.state.authOnboarding.tiktok_username || "" })

const sanitizeInstagram = (e: Event) => {
    const input = e.target as HTMLInputElement
    instagram.value = input.value.trim()
}

const sanitizeTikTok = (e: Event) => {
    const input = e.target as HTMLInputElement
    tiktok.value = input.value.trim()
}

const isSocialValid = computed(
    () => !!instagram.value.trim() || !!tiktok.value.trim()
)
const availabilityError = ref("")

const socialError = computed(() => {
    if (availabilityError.value) return availabilityError.value
    if (instagramMeta.touched || tiktokMeta.touched) {
        if (!isSocialValid.value) {
            return "Please enter at least one social media username."
        }
    }
    return ""
})

const onSubmit = handleSubmit(async () => {
    isLoading.value = true
    availabilityError.value = ""

    const ig = extractUsername(instagram.value)
    const tt = extractUsername(tiktok.value)

    try {
        const { data } = await api.post("user/check-usernames", {
            ig_username: ig || undefined,
            tiktok_username: tt || undefined,
        })

        if (!data.available) {
            const conflictLabels = data.conflicts.map((c: string) =>
                c === "ig_username" ? "Instagram" : "TikTok"
            )
            availabilityError.value = `${conflictLabels.join(" and ")} username${conflictLabels.length > 1 ? "s are" : " is"} already taken.`
            isLoading.value = false
            return
        }
    } catch {
        availabilityError.value = "Unable to verify username availability. Please try again."
        isLoading.value = false
        return
    }

    store.commit("authOnboarding/SET_SOCIALS", {
        ig_username: ig,
        tiktok_username: tt,
    })

    isLoading.value = false
    emit("next")
})

const goBack = () => {
    store.commit("authOnboarding/SET_SOCIALS", {
        ig_username: extractUsername(instagram.value),
        tiktok_username: extractUsername(tiktok.value),
    })

    emit("back")
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (__props.canGoBack)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("button", {
            onClick: goBack,
            class: "flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition"
          }, [...(_cache[3] || (_cache[3] = [
            _createElementVNode("span", null, "←", -1),
            _createElementVNode("span", null, "Back", -1)
          ]))])
        ]))
      : _createCommentVNode("", true),
    _cache[4] || (_cache[4] = _createElementVNode("div", { class: "flex flex-col gap-2" }, [
      _createElementVNode("h2", { class: "text-3xl font-semibold text-gray-700" }, "Link your Socials"),
      _createElementVNode("p", { class: "text-sm text-gray-400 font-medium" }, " Connect your social accounts so brands can understand your content and audience. ")
    ], -1)),
    _createVNode(FormInput, {
      modelValue: _unref(instagram),
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_isRef(instagram) ? (instagram).value = $event : null)),
      placeholder: "@Instagram_handle",
      error: _unref(instagramMeta).touched ? _unref(instagramError) : '',
      onInput: sanitizeInstagram
    }, null, 8, ["modelValue", "error"]),
    _createVNode(FormInput, {
      modelValue: _unref(tiktok),
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (_isRef(tiktok) ? (tiktok).value = $event : null)),
      placeholder: "@TikTok_handle",
      error: _unref(tiktokMeta).touched ? _unref(tiktokError) : '',
      onInput: sanitizeTikTok
    }, null, 8, ["modelValue", "error"]),
    (socialError.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_3, _toDisplayString(socialError.value), 1))
      : _createCommentVNode("", true),
    _createElementVNode("button", {
      disabled: !isSocialValid.value || isLoading.value,
      onClick: _cache[2] || (_cache[2] = 
//@ts-ignore
(...args) => (_unref(onSubmit) && _unref(onSubmit)(...args))),
      class: "w-full mt-6 bg-gradient-to-r from-purple-500 to-pink-400 text-white font-semibold py-3 rounded-xl disabled:opacity-40 disabled:cursor-not-allowed"
    }, " Continue ", 8, _hoisted_4)
  ]))
}
}

})